export type LanguageLevel = 'C2' | 'C1' | 'B2' | 'B1' | 'A2' | 'A1';

export interface LanguageEntry {
  language: string;
  level?: LanguageLevel | null;
}

export interface LanguageLevelOption {
  code: LanguageLevel;
  labelKey: string;
}

export const LANGUAGE_LEVEL_OPTIONS: LanguageLevelOption[] = [
  { code: 'C2', labelKey: 'personalData.languageLevels.C2' },
  { code: 'C1', labelKey: 'personalData.languageLevels.C1' },
  { code: 'B2', labelKey: 'personalData.languageLevels.B2' },
  { code: 'B1', labelKey: 'personalData.languageLevels.B1' },
  { code: 'A2', labelKey: 'personalData.languageLevels.A2' },
  { code: 'A1', labelKey: 'personalData.languageLevels.A1' },
];

const LEVEL_RANK: Record<string, number> = {
  C2: 6,
  C1: 5,
  B2: 4,
  B1: 3,
  A2: 2,
  A1: 1,
};

function levelRank(level: LanguageLevel | null | undefined): number {
  if (!level) return 0;
  return LEVEL_RANK[level] ?? 0;
}

export function sortLanguageEntries(
  entries: LanguageEntry[],
  getLabel: (code: string) => string = (code) => code,
): LanguageEntry[] {
  return [...entries].sort((a, b) => {
    const rankDiff = levelRank(b.level) - levelRank(a.level);
    if (rankDiff !== 0) return rankDiff;
    return getLabel(a.language).localeCompare(getLabel(b.language));
  });
}

export function formatLanguageLevel(level: LanguageLevel | null | undefined): string {
  return level ?? '';
}

function isLanguageEntry(value: unknown): value is LanguageEntry {
  return typeof value === 'object' && value !== null && 'language' in value;
}

export function normalizeToLanguageEntries(
  value: string[] | LanguageEntry[] | undefined | null,
): LanguageEntry[] {
  if (!value || value.length === 0) return [];
  return (value as (string | LanguageEntry)[]).map((element) =>
    isLanguageEntry(element) ? element : { language: element as string },
  );
}
